import { useState, useRef } from "react"
import {
  Popover,
  ActionIcon,
  PopoverProps,
  ActionIconProps,
} from "@mantine/core"
import { IconMoodHappy, IconProps } from "@tabler/icons-react"
import EmojiPicker, {
  EmojiClickData,
  EmojiStyle,
  Theme,
} from "emoji-picker-react"
import { useMantineColorScheme } from "@mantine/core"
import { ScrollContainer } from "../../../-components/scroll-container"

interface EmojiPickerDropdownProps extends Omit<
  PopoverProps,
  "opened" | "onChange"
> {
  onSelect: (emoji: EmojiClickData) => void
  focusTargetRef?: React.RefObject<HTMLElement | null>
  actionIconProps?: ActionIconProps
  iconProps?: IconProps
  opened?: boolean
  onOpenChange?: (open: boolean) => void
}

export function EmojiPickerDropdown({
  onSelect,
  focusTargetRef,
  actionIconProps,
  iconProps,
  opened,
  onOpenChange,
  ...props
}: EmojiPickerDropdownProps) {
  const isControlled = opened !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const { colorScheme } = useMantineColorScheme()

  const emojiPickerOpen = isControlled ? opened : uncontrolledOpen

  const setOpen = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value)
    } else {
      setUncontrolledOpen(value)
    }
  }

  const toggleOpen = () => setOpen(!emojiPickerOpen)

  const handleEmojiClick = (emoji: EmojiClickData) => {
    onSelect(emoji)
    setOpen(false)

    if (focusTargetRef?.current) {
      setTimeout(() => {
        focusTargetRef.current?.focus()
      }, 50)
    }
  }

  return (
    <Popover
      opened={emojiPickerOpen}
      onChange={setOpen}
      withArrow
      shadow="md"
      width={300}
      trapFocus
      withinPortal
      {...props}
    >
      <Popover.Target>
        <ActionIcon ref={buttonRef} onClick={toggleOpen} {...actionIconProps}>
          <IconMoodHappy {...iconProps} />
        </ActionIcon>
      </Popover.Target>

      <Popover.Dropdown p={0} style={{ pointerEvents: "all" }}>
        <ScrollContainer h={500} mah={"40dvh"}>
          <EmojiPicker
            nonce={window.__CSP_NONCE__}
            width="100%"
            height="100%"
            autoFocusSearch={false}
            previewConfig={{ showPreview: false }}
            style={{ pointerEvents: "all" }}
            open={emojiPickerOpen}
            onEmojiClick={handleEmojiClick}
            theme={colorScheme as Theme}
            emojiStyle={EmojiStyle.NATIVE}
          />
        </ScrollContainer>
      </Popover.Dropdown>
    </Popover>
  )
}
